import type { BeforeSendFn } from 'posthog-js'
import type { PostHog } from 'posthog-node'

import { posthogManager } from './manager'

type ErrorTracking = {
  error: Error | string
  properties?: Record<string, any>
  fingerprint: string
}

type SSRErrorTracking = ErrorTracking & {
  posthogInstance: PostHog
  userID?: string
}

export const errorTracking = ({
  error,
  properties = {},
  fingerprint
}: ErrorTracking) => {
  const posthog = posthogManager.getClientInstance()
  if (!posthog) return

  posthog?.captureException(error instanceof Error ? error.message : error, {
    $exception_fingerprint: fingerprint,
    error: error instanceof Error ? error : new Error(error),
    ...properties
  })
}
export const ssrErrorTracking = ({
  posthogInstance,
  userID,
  error,
  fingerprint,
  properties = {}
}: SSRErrorTracking) => {
  if (!posthogInstance) return

  posthogInstance?.captureException(
    error instanceof Error ? error.message : error,
    userID,
    {
      $exception_fingerprint: fingerprint,
      error: error instanceof Error ? error : new Error(error),
      ...properties
    }
  )
}

export const actionTracking = ({
  action,
  properties
}: {
  action: string
  properties?: Record<string, any>
}) => {
  const posthog = posthogManager.getClientInstance()
  if (!posthog) return

  posthog?.capture(action, {
    action,
    ...properties
  })
}

const errorsToIgnore: {
  type: string
  value: string
}[] = [
  // ref: https://trackjs.com/javascript-errors/object-not-found-matching-id-methodname-paramcount/
  { type: 'UnhandledRejection', value: 'MethodName:update, ParamCount:4' },
  {
    type: 'UnhandledRejection',
    value: 'Failed to load https://www.googletagmanager.com'
  }
]

export const beforeSend: BeforeSendFn = event => {
  if (event.event === '$exception') {
    const exceptionList = event.properties['$exception_list'] || []
    const exception = exceptionList.length > 0 ? exceptionList[0] : null

    if (
      exception &&
      errorsToIgnore.some(
        error =>
          error.type === exception['type'] &&
          exception['value'].includes(error.value)
      )
    ) {
      return null // This prevents the event from being sent
    }
  }
  return event
}
